import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, LogOut, RefreshCw, Send, Truck, Users, Euro, Package, CheckCircle2, Download, AlertCircle, TrendingUp, Loader2, Mic, MessageCircle, Mail, Video, ExternalLink, Trash2 } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import NewsletterTab from "@/components/admin/NewsletterTab";
import SnippetsTab from "@/components/admin/SnippetsTab";
import VoiceTab from "@/components/admin/VoiceTab";
import ProspectsTab from "@/components/admin/ProspectsTab";
import RevenueTab from "@/components/admin/RevenueTab";
import SourcesTab from "@/components/admin/SourcesTab";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configurations stables des charts — module-level pour éviter les re-renders Recharts inutiles
const REV_CHART_MARGIN = { top: 10, right: 16, left: 0, bottom: 0 };
const REV_AXIS_TICK = { fontSize: 11, fill: "#7c7060" };
const REV_TOOLTIP_STYLE = { background: "#fff", border: "1px solid #d9cfb8", borderRadius: 12, fontSize: 12 };
const REV_LEGEND_STYLE = { fontSize: 12 };
const REV_DOT_R = { r: 4 };
const STORAGE_KEY = "papicoo_admin_pwd";

const fmtEur = (n) => `${(Number(n) || 0).toFixed(2)} €`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

function Login({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(STORAGE_KEY, pwd);
      onLogin(pwd);
      toast.success(T("Salaam, Papicoo. Accès accordé."));
    } catch (err) {
      toast.error(err?.response?.data?.detail || T("Accès refusé."));
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="papicoo-card p-8 w-full max-w-md space-y-5">
        <div className="text-center">
          <Shield size={36} className="mx-auto text-[color:var(--papicoo-gold)]" />
          <h1 className="font-display text-3xl mt-3">{T("Poste de pilotage")}</h1>
          <p className="text-sm text-[color:var(--papicoo-muted)] mt-1 italic">{T("Accès réservé — Papicoo seul.")}</p>
        </div>
        <input
          data-testid="admin-password-input"
          type="password"
          autoFocus
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder={T("Mot de passe admin")}
          className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
        />
        <button data-testid="admin-login-submit" disabled={loading || !pwd} className="w-full px-5 py-3 rounded-full papicoo-btn-primary text-sm disabled:opacity-50">
          {loading ? T("Vérification…") : "Entrer"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="papicoo-card p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">
        <Icon size={14} className="text-[color:var(--papicoo-gold)]" />
        <span>{label}</span>
      </div>
      <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-2">{value}</div>
      {sub && <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function TransfersTab({ pwd }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/transfers`, { headers: { "X-Admin-Password": pwd } });
      setRows(data);
    } catch { toast.error(T("Échec de chargement.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markPayout = async (sessionId, current) => {
    const ref = current ? "" : (window.prompt(T("Référence Wave / Orange Money (optionnel) :")) || "");
    try {
      await axios.patch(
        `${API}/admin/transfers/${sessionId}/payout`,
        { payout_done: !current, payout_reference: ref || null },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(current ? T("Payout déclassé.") : T("Payout marqué effectué."));
      load();
    } catch { toast.error(T("Échec mise à jour.")); }
  };

  return (
    <div data-testid="admin-transfers-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">{T("Transferts diaspora")}</div>
        <button onClick={load} data-testid="reload-transfers" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)]">
          <RefreshCw size={12} /> {T("Rafraîchir")}
        </button>
      </div>
      <div className="papicoo-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--papicoo-sand-deep)]/60 text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">
            <tr>
              <th className="text-left p-3">{T("Date")}</th>
              <th className="text-left p-3">{T("Expéditeur")}</th>
              <th className="text-left p-3">{T("Destinataire")}</th>
              <th className="text-left p-3">{T("Méthode")}</th>
              <th className="text-right p-3">{T("Envoi")}</th>
              <th className="text-right p-3">{T("Frais")}</th>
              <th className="text-center p-3">{T("Paiement")}</th>
              <th className="text-center p-3">{T("Payout")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="p-6 text-center italic text-[color:var(--papicoo-muted)]">{T("Chargement…")}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="8" className="p-6 text-center italic text-[color:var(--papicoo-muted)]">{T("Aucun transfert.")}</td></tr>}
            {rows.map((r) => {
              const m = r.metadata || {};
              const paid = r.payment_status === "paid";
              return (
                <tr key={r.session_id} data-testid={`transfer-row-${r.session_id}`} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/30">
                  <td className="p-3 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="p-3"><div className="font-medium">{m.sender_name}</div><div className="text-xs text-[color:var(--papicoo-muted)]">{m.sender_email}</div></td>
                  <td className="p-3"><div className="font-medium">{m.recipient_name}</div><div className="text-xs text-[color:var(--papicoo-muted)]">{m.recipient_phone}</div></td>
                  <td className="p-3 text-xs">{m.payout_method}</td>
                  <td className="p-3 text-right">{fmtEur(r.send_eur || m.send_eur)}</td>
                  <td className="p-3 text-right text-xs">{fmtEur(r.fee_eur || m.fee_eur)}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {paid ? T("Payé") : (r.payment_status || T("initié"))}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    {paid ? (
                      <button
                        data-testid={`toggle-payout-${r.session_id}`}
                        onClick={() => markPayout(r.session_id, !!r.payout_done)}
                        className={`text-[11px] px-2.5 py-1 rounded-full ${r.payout_done ? "bg-emerald-600 text-white" : "bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/60"}`}
                      >
                        {r.payout_done ? T("✓ Effectué") : T("Marquer envoyé")}
                      </button>
                    ) : <span className="text-xs text-[color:var(--papicoo-muted)]">—</span>}
                    {r.payout_reference && <div className="text-[10px] text-[color:var(--papicoo-muted)] mt-0.5">{r.payout_reference}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DropshipTab({ pwd }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dropship`, { headers: { "X-Admin-Password": pwd } });
      setRows(data);
    } catch { toast.error(T("Échec de chargement.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markShipped = async (sessionId, current) => {
    const tracking = current ? "" : (window.prompt("Numéro de suivi (optionnel) :") || "");
    const carrier = current ? "" : (window.prompt("Transporteur (optionnel) :") || "");
    try {
      await axios.patch(
        `${API}/admin/dropship/${sessionId}/shipment`,
        { shipped: !current, tracking_number: tracking || null, carrier: carrier || null },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(current ? T("Expédition déclassée.") : T("Expédition marquée."));
      load();
    } catch { toast.error(T("Échec mise à jour.")); }
  };

  return (
    <div data-testid="admin-dropship-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">{T("Commandes dropship")}</div>
        <button onClick={load} data-testid="reload-dropship" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)]">
          <RefreshCw size={12} /> {T("Rafraîchir")}
        </button>
      </div>
      <div className="papicoo-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--papicoo-sand-deep)]/60 text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">
            <tr>
              <th className="text-left p-3">{T("Date")}</th>
              <th className="text-left p-3">{T("Produit")}</th>
              <th className="text-left p-3">{T("Acheteur")}</th>
              <th className="text-left p-3">{T("Livraison")}</th>
              <th className="text-right p-3">{T("Montant")}</th>
              <th className="text-center p-3">{T("Paiement")}</th>
              <th className="text-center p-3">{T("Expédition")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-6 text-center italic text-[color:var(--papicoo-muted)]">{T("Chargement…")}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center italic text-[color:var(--papicoo-muted)]">{T("Aucune commande.")}</td></tr>}
            {rows.map((r) => {
              const m = r.metadata || {};
              const paid = r.payment_status === "paid";
              return (
                <tr key={r.session_id} data-testid={`dropship-row-${r.session_id}`} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/30">
                  <td className="p-3 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="p-3"><div className="font-medium">{m.product_name}</div><div className="text-xs text-[color:var(--papicoo-muted)]">qty {m.quantity || r.quantity}</div></td>
                  <td className="p-3"><div className="font-medium">{m.buyer_name}</div><div className="text-xs text-[color:var(--papicoo-muted)]">{m.buyer_email}</div></td>
                  <td className="p-3 text-xs"><div>{m.shipping_address}</div><div>{m.shipping_city}, {m.shipping_country}</div><div>{m.shipping_phone}</div></td>
                  <td className="p-3 text-right">{fmtEur(r.amount_eur)}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {paid ? T("Payé") : (r.payment_status || T("initié"))}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    {paid ? (
                      <button
                        data-testid={`toggle-shipment-${r.session_id}`}
                        onClick={() => markShipped(r.session_id, !!r.shipped)}
                        className={`text-[11px] px-2.5 py-1 rounded-full ${r.shipped ? "bg-emerald-600 text-white" : "bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/60"}`}
                      >
                        {r.shipped ? T("✓ Expédié") : T("Marquer expédié")}
                      </button>
                    ) : <span className="text-xs text-[color:var(--papicoo-muted)]">—</span>}
                    {r.tracking_number && <div className="text-[10px] text-[color:var(--papicoo-muted)] mt-0.5">{r.carrier ? `${r.carrier} · ` : ""}{r.tracking_number}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTab({ pwd }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/users`, { headers: { "X-Admin-Password": pwd } });
        setRows(data);
      } catch { toast.error(T("Échec de chargement.")); }
      finally { setLoading(false); }
    })();
  }, [pwd]);

  return (
    <div data-testid="admin-users-tab">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)] mb-3">{T("Diaspora connectée")}</div>
      <div className="papicoo-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--papicoo-sand-deep)]/60 text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">
            <tr>
              <th className="text-left p-3">{T("Prénom")}</th>
              <th className="text-left p-3">{T("Adressage")}</th>
              <th className="text-left p-3">{T("User ID")}</th>
              <th className="text-left p-3">{T("Vu pour la 1ʳᵉ fois")}</th>
              <th className="text-left p-3">{T("Dernière activité")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="p-6 text-center italic text-[color:var(--papicoo-muted)]">{T("Chargement…")}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="5" className="p-6 text-center italic text-[color:var(--papicoo-muted)]">{T("Aucun profil enregistré.")}</td></tr>}
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`user-row-${r.user_id}`} className="border-t border-[color:var(--papicoo-border)]">
                <td className="p-3 font-medium">{r.name || <span className="italic text-[color:var(--papicoo-muted)]">{T("anonyme")}</span>}</td>
                <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[color:var(--papicoo-sand-deep)]">{r.gender || "—"}</span></td>
                <td className="p-3 text-[10px] font-mono text-[color:var(--papicoo-muted)] break-all">{r.user_id}</td>
                <td className="p-3 text-xs">{fmtDate(r.created_at)}</td>
                <td className="p-3 text-xs">{fmtDate(r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoutsTab({ pwd, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/payouts/today`, { headers: { "X-Admin-Password": pwd } });
      setRows(data);
    } catch { toast.error(T("Échec de chargement.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markDone = async (sessionId) => {
    const ref = window.prompt(T("Référence Wave / Orange Money (optionnel) :")) || "";
    try {
      await axios.patch(
        `${API}/admin/transfers/${sessionId}/payout`,
        { payout_done: true, payout_reference: ref || null },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(T("Payout marqué effectué 🌹"));
      load();
      onChange?.();
    } catch { toast.error(T("Échec mise à jour.")); }
  };

  const totalDue = rows.reduce((s, r) => s + Number(r.send_eur || r.metadata?.send_eur || 0), 0);

  return (
    <div data-testid="admin-payouts-tab">
      <div className="papicoo-card p-5 mb-4 bg-gradient-to-br from-[color:var(--papicoo-sand-deep)] to-white">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">{T("Ta to-do du jour")}</div>
        <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">
          {rows.length === 0 ? T("Aucun payout en attente. 🌹") : `${rows.length} payout${rows.length > 1 ? "s" : ""} à envoyer aujourd'hui`}
        </div>
        {rows.length > 0 && (
          <div className="text-sm text-[color:var(--papicoo-muted)] mt-1">
            {T("Total à transférer aux destinataires :")} <strong className="text-[color:var(--papicoo-ink)]">{totalDue.toFixed(2)} €</strong>
          </div>
        )}
      </div>

      {!loading && rows.length > 0 && (
        <div className="papicoo-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--papicoo-sand-deep)]/60 text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">
              <tr>
                <th className="text-left p-3">{T("Payé le")}</th>
                <th className="text-left p-3">{T("Destinataire")}</th>
                <th className="text-left p-3">{T("Méthode")}</th>
                <th className="text-right p-3">{T("À envoyer")}</th>
                <th className="text-left p-3">{T("Expéditeur")}</th>
                <th className="text-center p-3">{T("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = r.metadata || {};
                return (
                  <tr key={r.session_id} data-testid={`payout-row-${r.session_id}`} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/30">
                    <td className="p-3 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="p-3">
                      <div className="font-medium">{m.recipient_name}</div>
                      <div className="text-xs font-mono text-[color:var(--papicoo-muted)]">{m.recipient_phone}</div>
                    </td>
                    <td className="p-3">
                      <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--papicoo-gold)]/20 text-[color:var(--papicoo-ink)]">
                        {m.payout_method}
                      </span>
                    </td>
                    <td className="p-3 text-right font-display text-lg text-[color:var(--papicoo-ink)]">
                      {fmtEur(r.send_eur || m.send_eur)}
                    </td>
                    <td className="p-3 text-xs">
                      <div>{m.sender_name}</div>
                      <div className="text-[color:var(--papicoo-muted)]">{m.sender_email}</div>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        data-testid={`mark-payout-${r.session_id}`}
                        onClick={() => markDone(r.session_id)}
                        className="text-xs px-3 py-1.5 rounded-full papicoo-btn-primary inline-flex items-center gap-1"
                      >
                        <CheckCircle2 size={12} /> {T("Marquer envoyé")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {loading && <div className="text-center italic text-[color:var(--papicoo-muted)] p-6">{T("Chargement…")}</div>}
    </div>
  );
}

function WhatsAppTab({ pwd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp`, { headers: { "X-Admin-Password": pwd } });
      setData(data);
    } catch (e) { toast.error(T("Échec chargement clics WhatsApp")); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-whatsapp-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Clics WhatsApp · funnel")}</h2>
        <button onClick={load} disabled={loading} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {T("Rafraîchir")}
        </button>
      </div>
      {data ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="papicoo-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("Total clics")}</div>
              <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">{data.total}</div>
            </div>
            {data.by_source.slice(0, 3).map((s) => (
              <div key={s.source} className="papicoo-card p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] truncate">{s.source}</div>
                <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">{s.count}</div>
              </div>
            ))}
          </div>
          <div className="papicoo-card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[color:var(--papicoo-muted)] text-[11px] uppercase tracking-[0.15em]">
                <tr><th className="text-left p-3">{T("Date")}</th><th className="text-left p-3">{T("Source")}</th><th className="text-left p-3">{T("Utilisateur")}</th><th className="text-left p-3">{T("Dernier message")}</th></tr>
              </thead>
              <tbody>
                {data.clicks.length === 0 ? (
                  <tr><td colSpan={4} className="text-center italic text-[color:var(--papicoo-muted)] py-8">{T("Aucun clic encore. Le bouton flottant est en place sur tout le site.")}</td></tr>
                ) : data.clicks.map((c) => (
                  <tr key={c.id} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/20">
                    <td className="p-3 text-[11px] text-[color:var(--papicoo-muted)]">{fmtDate(c.created_at)}</td>
                    <td className="p-3"><span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)]">{c.source}</span></td>
                    <td className="p-3">{c.user_name || "—"}</td>
                    <td className="p-3 text-[12px] text-[color:var(--papicoo-muted)] italic max-w-md truncate">{c.last_message_excerpt || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : <div className="text-center py-12"><Loader2 className="animate-spin mx-auto" /></div>}
    </div>
  );
}

function EmailTab({ pwd }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dns, setDns] = useState(null);
  const [dnsLoading, setDnsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email/status`, { headers: { "X-Admin-Password": pwd } });
      setStatus(data);
    } catch (e) { toast.error(T("Échec chargement statut email")); } finally { setLoading(false); }
  };
  const checkDns = async () => {
    setDnsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email/dns-check`, { headers: { "X-Admin-Password": pwd } });
      setDns(data);
      toast[data.all_propagated ? "success" : "info"](data.hint);
    } catch (e) { toast.error(T("Échec sonde DNS")); } finally { setDnsLoading(false); }
  };
  useEffect(() => { load(); checkDns(); }, []);

  return (
    <div data-testid="admin-email-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Domaine email · Resend")}</h2>
        <button onClick={load} disabled={loading} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {T("Rafraîchir")}
        </button>
      </div>
      {status && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="papicoo-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("From configuré")}</div>
              <div className="text-base text-[color:var(--papicoo-ink)] mt-1 break-all">{status.from || "—"}</div>
            </div>
            <div className="papicoo-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("Domaine")}</div>
              <div className="text-base text-[color:var(--papicoo-ink)] mt-1">{status.domain || "—"}</div>
            </div>
            <div className="papicoo-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("Statut")}</div>
              <div className={`mt-1 inline-flex items-center gap-2 ${status.verified ? "text-emerald-700" : "text-amber-700"}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${status.verified ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="text-sm font-medium">{status.verified ? T("Vérifié ✓") : (status.status || T("Non vérifié"))}</span>
              </div>
            </div>
          </div>

          {/* Sonde DNS publique (8.8.8.8 / 1.1.1.1) — fonctionne même avec clé Resend restricted */}
          <div className="papicoo-card p-5 mb-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display text-lg text-[color:var(--papicoo-ink)] inline-flex items-center gap-2">
                  Sonde DNS publique
                  {dns && (dns.all_propagated
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{T("Tous propagés ✓")}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{T("En attente")}</span>)}
                </div>
                <div className="text-[11px] text-[color:var(--papicoo-muted)]">{T("Vérifie en direct si OVH a publié tes records (cache Google + Cloudflare).")}</div>
              </div>
              <button
                data-testid="dns-check-btn"
                onClick={checkDns}
                disabled={dnsLoading}
                className="text-xs px-3 py-1.5 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] inline-flex items-center gap-1"
              >
                <RefreshCw size={12} className={dnsLoading ? "animate-spin" : ""} /> {T("Sonder maintenant")}
              </button>
            </div>
            {dns && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[color:var(--papicoo-muted)]">
                    <tr><th className="text-left p-2">{T("Record")}</th><th className="text-left p-2">{T("Cible")}</th><th className="text-left p-2">{T("État")}</th></tr>
                  </thead>
                  <tbody>
                    {dns.checks.map((c) => (
                      <tr key={`${c.type}-${T(c.name)}`} className="border-t border-[color:var(--papicoo-border)]">
                        <td className="p-2 font-medium">{T(c.label)} <span className="text-[color:var(--papicoo-muted)] font-normal">({c.type})</span></td>
                        <td className="p-2 font-mono break-all">{T(c.name)}</td>
                        <td className="p-2">
                          {c.ok ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {T("Trouvé")}</span>
                          ) : c.found ? (
                            <span className="inline-flex items-center gap-1 text-amber-700"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {T("Valeur inattendue")}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {T("Absent")}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 text-xs italic text-[color:var(--papicoo-muted)]">{T(dns.hint)}</div>
              </div>
            )}
          </div>

          {!status.verified && (
            <div className="papicoo-card p-5 mb-5 border-l-4 border-[color:var(--papicoo-gold)]">
              <div className="text-sm text-[color:var(--papicoo-ink)] font-medium mb-2">{T("📋 Pas-à-pas OVH (ton registrar)")}</div>
              <ol className="text-xs text-[color:var(--papicoo-muted)] list-decimal list-inside space-y-1.5 leading-relaxed">
                <li>{T("Va sur")} <a href="https://www.ovh.com/manager/#/web/domain/papicoo.fr/zone" target="_blank" rel="noopener noreferrer" className="papicoo-link-underline">{T("Manager OVH → papicoo.fr → Zone DNS")}</a></li>
                <li>{T("Clique «")} <strong>{T("Ajouter une entrée")}</strong> {T("» pour chacun des 4 records ci-dessous")}</li>
                <li>{T("Pour chaque record, copie")} <strong>{T("EXACTEMENT")}</strong> {T("la valeur affichée dans")} <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="papicoo-link-underline">{T("Resend → papicoo.fr")}</a> {T("(clique sur le record pour voir la valeur complète)")}</li>
                <li>{T("Attends 5–60 min puis clique « Sonder maintenant » ci-dessus jusqu'à ce que les 4 passent en vert ✓")}</li>
                <li>{T("Reviens sur Resend et clique «")} <strong>{T("Verify")}</strong> » — statut passera &quot;verified&quot;</li>
              </ol>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[color:var(--papicoo-muted)]">
                    <tr><th className="text-left p-2">{T("Type OVH")}</th><th className="text-left p-2">{T("Sous-domaine")}</th><th className="text-left p-2">{T("Cible / Valeur")}</th><th className="text-left p-2">{T("Notes")}</th></tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-[color:var(--papicoo-border)]">
                      <td className="p-2 font-mono font-medium">{T("TXT")}</td>
                      <td className="p-2 font-mono">{T("resend._domainkey")}</td>
                      <td className="p-2 italic text-[color:var(--papicoo-muted)]">p=MIGfMA0GCSqG… (clé DKIM longue, copie depuis Resend)</td>
                      <td className="p-2 text-[11px]">{T("Sans guillemets dans OVH")}</td>
                    </tr>
                    <tr className="border-t border-[color:var(--papicoo-border)]">
                      <td className="p-2 font-mono font-medium">{T("MX")}</td>
                      <td className="p-2 font-mono">{T("send")}</td>
                      <td className="p-2 font-mono">{T("feedback-smtp.eu-west-1.amazonses.com.")}</td>
                      <td className="p-2 text-[11px]">{T("Priorité")} <strong>10</strong> {T("· point final inclus")}</td>
                    </tr>
                    <tr className="border-t border-[color:var(--papicoo-border)]">
                      <td className="p-2 font-mono font-medium">{T("TXT")}</td>
                      <td className="p-2 font-mono">{T("send")}</td>
                      <td className="p-2 font-mono">v=spf1 include:amazonses.com ~all</td>
                      <td className="p-2 text-[11px]">{T("SPF — sans guillemets")}</td>
                    </tr>
                    <tr className="border-t border-[color:var(--papicoo-border)]">
                      <td className="p-2 font-mono font-medium">{T("TXT")}</td>
                      <td className="p-2 font-mono">{T("_dmarc")}</td>
                      <td className="p-2 font-mono">v=DMARC1; p=none;</td>
                      <td className="p-2 text-[11px]">{T("DMARC permissif")}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-[11px] italic text-[color:var(--papicoo-muted)]">
                {T("💡 Astuce OVH : si OVH ajoute automatiquement")} <code>{T(".papicoo.fr")}</code> {T("au sous-domaine, c'est normal — laisse-le faire. Si OVH te demande la valeur DKIM avec des guillemets, mets-la SANS guillemets (OVH les rajoute pour toi).")}
              </div>
            </div>
          )}
          {status.verified && (
            <div className="papicoo-card p-5 border-l-4 border-emerald-500">
              <div className="text-sm text-[color:var(--papicoo-ink)] font-medium mb-1">{T("✅ Tout est en règle")}</div>
              <p className="text-sm text-[color:var(--papicoo-muted)]">{T("Les emails partent depuis")} <strong>{status.from}</strong>{T(". Aucun fallback nécessaire.")}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NihmaAdminTab({ pwd }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/nihma/videos`);
      setVideos(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (vid) => {
    if (!window.confirm("Supprimer cette transcription ?")) return;
    try {
      await axios.delete(`${API}/admin/nihma/${vid}`, { headers: { "X-Admin-Password": pwd } });
      toast.success(T("Supprimé."));
      load();
    } catch (e) { toast.error(T("Échec suppression.")); }
  };

  return (
    <div data-testid="admin-nihma-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Bibliothèque Nihma TV")}</h2>
        <div className="flex gap-2">
          <a href="/nihma-tv" className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
            <ExternalLink size={12} /> {T("Ouvrir la page publique")}
          </a>
          <button onClick={load} disabled={loading} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {T("Rafraîchir")}
          </button>
        </div>
      </div>
      <div className="papicoo-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[color:var(--papicoo-muted)] text-[11px] uppercase tracking-[0.15em]">
            <tr>
              <th className="text-left p-3">{T("Titre")}</th>
              <th className="text-left p-3">{T("Source")}</th>
              <th className="text-left p-3">{T("Durée")}</th>
              <th className="text-left p-3">{T("Date")}</th>
              <th className="text-left p-3">{T("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {videos.length === 0 ? (
              <tr><td colSpan={5} className="text-center italic text-[color:var(--papicoo-muted)] py-8">{T("Aucune vidéo transcrite encore.")}</td></tr>
            ) : videos.map((v) => (
              <tr key={v.video_id} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/20">
                <td className="p-3 max-w-md"><div className="font-medium truncate">{T(v.title)}</div></td>
                <td className="p-3 text-xs">{v.uploader || "Upload"}</td>
                <td className="p-3 text-xs font-mono">{v.duration ? `${Math.floor(v.duration / 60)}:${String(Math.floor(v.duration % 60)).padStart(2, "0")}` : "—"}</td>
                <td className="p-3 text-[11px] text-[color:var(--papicoo-muted)]">{fmtDate(v.created_at)}</td>
                <td className="p-3">
                  <button data-testid={`nihma-del-${v.video_id}`} onClick={() => del(v.video_id)} className="text-xs inline-flex items-center gap-1 text-red-600 hover:text-red-700">
                    <Trash2 size={12} /> {T("Supprimer")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function Admin() {
  const [pwd, setPwd] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("revenue");
  const [revenueData, setRevenueData] = useState([]);

  const loadOverview = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/overview`, { headers: { "X-Admin-Password": pwd } });
      setOverview(data);
      const { data: rev } = await axios.get(`${API}/admin/revenue/monthly`, { headers: { "X-Admin-Password": pwd } });
      setRevenueData(rev);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setPwd("");
        toast.error("Session expirée — reconnecte-toi.");
      }
    }
  };
  useEffect(() => { if (pwd) loadOverview(); }, [pwd]);

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPwd("");
  };

  const downloadCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/admin/${kind}/export.csv`, {
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `papicoo_${kind}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(T("Export téléchargé."));
    } catch { toast.error(T("Export impossible.")); }
  };

  if (!pwd) return <Login onLogin={setPwd} />;

  return (
    <div data-testid="page-admin" className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Poste de pilotage")}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[color:var(--papicoo-ink)] leading-tight">{T("Papicoo Admin 🌹")}</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)]">
          <LogOut size={12} /> {T("Déconnexion")}
        </button>
      </div>

      {overview && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            testid="stat-transfer-revenue"
            icon={Euro}
            label={T("CA Transferts (payé)")}
            value={fmtEur(overview.transfers.paid_revenue_eur)}
            sub={`Dont frais : ${fmtEur(overview.transfers.paid_fees_eur)} · ${overview.transfers.payouts_pending} payout(s) en attente`}
          />
          <StatCard
            testid="stat-dropship-revenue"
            icon={Package}
            label={T("CA Dropship (payé)")}
            value={fmtEur(overview.dropship.paid_revenue_eur)}
            sub={`${overview.dropship.shipments_pending} commande(s) à expédier`}
          />
          <StatCard
            testid="stat-transactions"
            icon={CheckCircle2}
            label={T("Transactions payées")}
            value={overview.transfers.paid_count + overview.dropship.paid_count}
            sub={`${overview.transfers.total_count + overview.dropship.total_count} créées au total`}
          />
          <StatCard
            testid="stat-users"
            icon={Users}
            label={T("Diaspora connectée")}
            value={overview.users.profiles}
            sub={`${overview.users.sessions} conversations chat`}
          />
        </div>
      )}

      {/* Graphe d'évolution mensuelle */}
      {revenueData.length > 0 && (
        <div data-testid="revenue-chart-card" className="papicoo-card p-5 mb-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)] mb-3">
            <TrendingUp size={14} className="text-[color:var(--papicoo-gold)]" />
            {T("Évolution mensuelle du CA (12 derniers mois)")}
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={revenueData} margin={REV_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5dccb" />
                <XAxis dataKey="month" tick={REV_AXIS_TICK} />
                <YAxis tick={REV_AXIS_TICK} tickFormatter={(v) => `${v} €`} />
                <Tooltip
                  contentStyle={REV_TOOLTIP_STYLE}
                  formatter={(v) => `${Number(v).toFixed(2)} €`}
                />
                <Legend wrapperStyle={REV_LEGEND_STYLE} />
                <Line type="monotone" dataKey="transfer" name="Transferts" stroke="#0F291E" strokeWidth={2.5} dot={REV_DOT_R} />
                <Line type="monotone" dataKey="dropship" name="Dropship" stroke="#D4AF37" strokeWidth={2.5} dot={REV_DOT_R} />
                <Line type="monotone" dataKey="total" name="Total" stroke="#B0413E" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-5 border-b border-[color:var(--papicoo-border)] flex-wrap">
        {[
          { id: "revenue", label: "Revenus", icon: Euro },
          { id: "payouts", label: "Payouts du jour", icon: AlertCircle },
          { id: "transfers", label: "Transferts", icon: Send },
          { id: "dropship", label: "Dropship", icon: Truck },
          { id: "users", label: "Utilisateurs", icon: Users },
          { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
          { id: "email", label: "Email", icon: Mail },
          { id: "nihma", label: "Nihma TV", icon: Video },
          { id: "newsletter", label: "Newsletter", icon: Mail },
          { id: "snippets", label: "HTML/CSS", icon: Mic },
          { id: "voice", label: "Voix", icon: Mic },
          { id: "prospects", label: "Prospects", icon: Users },
          { id: "sources", label: "Sources", icon: TrendingUp },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px ${tab === t.id ? "border-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)] font-medium" : "border-transparent text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]"}`}
          >
            <t.icon size={14} /> {T(t.label)}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {(tab === "transfers" || tab === "payouts") && (
            <button data-testid="export-transfers-csv" onClick={() => downloadCsv("transfers")} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
              <Download size={12} /> {T("Export CSV transferts")}
            </button>
          )}
          {tab === "dropship" && (
            <button data-testid="export-dropship-csv" onClick={() => downloadCsv("dropship")} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
              <Download size={12} /> {T("Export CSV dropship")}
            </button>
          )}
        </div>
      </div>

      {tab === "payouts" && <PayoutsTab pwd={pwd} onChange={loadOverview} />}
      {tab === "transfers" && <TransfersTab pwd={pwd} />}
      {tab === "dropship" && <DropshipTab pwd={pwd} />}
      {tab === "users" && <UsersTab pwd={pwd} />}
      {tab === "whatsapp" && <WhatsAppTab pwd={pwd} />}
      {tab === "email" && <EmailTab pwd={pwd} />}
      {tab === "nihma" && <NihmaAdminTab pwd={pwd} />}
      {tab === "newsletter" && <NewsletterTab pwd={pwd} />}
      {tab === "snippets" && <SnippetsTab pwd={pwd} />}
      {tab === "voice" && <VoiceTab pwd={pwd} />}
      {tab === "prospects" && <ProspectsTab pwd={pwd} />}
      {tab === "sources" && <SourcesTab password={pwd} />}
      {tab === "revenue" && <RevenueTab pwd={pwd} />}
    </div>
  );
}
